import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Shield,
  Lock,
  Download,
  Trash2,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Eye,
  Mail,
  Bell,
  Smartphone,
  History,
  FileText,
  Globe,
  Clock
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function PrivacySettings() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [consents, setConsents] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    call_recording: false,
    data_sharing: false,
    personalization: false
  });
  const [consentHistory, setConsentHistory] = useState([]);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [dsarType, setDsarType] = useState("access");
  const [dsarDetails, setDsarDetails] = useState("");
  const [dsarRequests, setDsarRequests] = useState([]);

  useEffect(() => {
    loadConsents();
    loadDsarRequests();
  }, []);

  const loadConsents = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/privacy/consents/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setConsents(data.consents || consents);
      }

      // Load history
      const historyRes = await fetch(`${API_URL}/api/privacy/consents/${user.id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (historyRes.ok) {
        const data = await historyRes.json();
        setConsentHistory(data.history || []);
      }
    } catch (error) {
      toast.error("Failed to load privacy settings");
    } finally {
      setLoading(false);
    }
  };

  const loadDsarRequests = async () => {
    // In a real app, you'd have an endpoint to get user's own DSARs
    // For now, we'll leave this empty
    setDsarRequests([]);
  };

  const saveConsents = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/privacy/consents/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(consents)
      });

      if (res.ok) {
        toast.success("Privacy preferences saved");
        loadConsents();
      } else {
        toast.error("Failed to save preferences");
      }
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const exportData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/privacy/export/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `speakr-data-export-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Data exported successfully");
        setShowExportDialog(false);
      } else {
        toast.error("Failed to export data");
      }
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const requestDeletion = async () => {
    try {
      const res = await fetch(`${API_URL}/api/privacy/delete/${user.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success("Deletion request submitted. We'll process it within 30 days.");
        setShowDeleteDialog(false);
      } else {
        toast.error("Failed to submit deletion request");
      }
    } catch (error) {
      toast.error("Failed to submit deletion request");
    }
  };

  const submitDsar = async () => {
    try {
      const res = await fetch(`${API_URL}/api/privacy/dsar?user_id=${user.id}&user_email=${user.email}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          request_type: dsarType,
          details: dsarDetails
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Request submitted. Reference: ${data.id.slice(0, 8)}. Deadline: ${new Date(data.deadline).toLocaleDateString()}`);
        setDsarDetails("");
      } else {
        toast.error("Failed to submit request");
      }
    } catch (error) {
      toast.error("Failed to submit request");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto" data-testid="privacy-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Privacy & Data</h1>
        <p className="text-slate-500 mt-1">Manage your data preferences and privacy settings</p>
      </div>

      {/* Consent Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Data Usage Preferences
          </CardTitle>
          <CardDescription>Control how your data is used on Speakr</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Essential - Always On */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-slate-600" />
              <div>
                <Label className="font-medium">Essential Services</Label>
                <p className="text-sm text-slate-500">Required for core platform functionality</p>
              </div>
            </div>
            <Switch checked={true} disabled />
          </div>

          {/* Analytics */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-blue-600" />
              <div>
                <Label className="font-medium">Usage Analytics</Label>
                <p className="text-sm text-slate-500">Help us improve by sharing usage data</p>
              </div>
            </div>
            <Switch
              checked={consents.analytics}
              onCheckedChange={(v) => setConsents({ ...consents, analytics: v })}
              data-testid="analytics-toggle"
            />
          </div>

          {/* Marketing */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-purple-600" />
              <div>
                <Label className="font-medium">Marketing Communications</Label>
                <p className="text-sm text-slate-500">Receive newsletters, tips, and promotions</p>
              </div>
            </div>
            <Switch
              checked={consents.marketing}
              onCheckedChange={(v) => setConsents({ ...consents, marketing: v })}
              data-testid="marketing-toggle"
            />
          </div>

          {/* Call Recording */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-emerald-600" />
              <div>
                <Label className="font-medium">Call Recording</Label>
                <p className="text-sm text-slate-500">Allow AI agents to record calls for quality</p>
              </div>
            </div>
            <Switch
              checked={consents.call_recording}
              onCheckedChange={(v) => setConsents({ ...consents, call_recording: v })}
              data-testid="recording-toggle"
            />
          </div>

          {/* Data Sharing */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-amber-600" />
              <div>
                <Label className="font-medium">Third-Party Data Sharing</Label>
                <p className="text-sm text-slate-500">Share data with partners for enhanced services</p>
              </div>
            </div>
            <Switch
              checked={consents.data_sharing}
              onCheckedChange={(v) => setConsents({ ...consents, data_sharing: v })}
              data-testid="sharing-toggle"
            />
          </div>

          {/* AI Personalization */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-pink-600" />
              <div>
                <Label className="font-medium">AI Personalization</Label>
                <p className="text-sm text-slate-500">Allow AI to personalize your experience</p>
              </div>
            </div>
            <Switch
              checked={consents.personalization}
              onCheckedChange={(v) => setConsents({ ...consents, personalization: v })}
              data-testid="personalization-toggle"
            />
          </div>

          <Button onClick={saveConsents} disabled={saving} data-testid="save-preferences-btn">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Save Preferences
          </Button>
        </CardContent>
      </Card>

      {/* Data Rights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Your Data Rights
          </CardTitle>
          <CardDescription>Exercise your privacy rights under PIPEDA, GDPR, and CCPA</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Export Data */}
            <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
              <DialogTrigger asChild>
                <div className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition" data-testid="export-data-card">
                  <Download className="w-8 h-8 text-blue-600 mb-3" />
                  <h3 className="font-medium">Export My Data</h3>
                  <p className="text-sm text-slate-500 mt-1">Download all your data in JSON format</p>
                </div>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Export Your Data</DialogTitle>
                  <DialogDescription>
                    Download a copy of all data we have about you, including profile, call history, and preferences.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-sm text-slate-500">Your export will include:</p>
                  <ul className="list-disc list-inside text-sm text-slate-500 mt-2 space-y-1">
                    <li>Account information</li>
                    <li>Call history and transcripts</li>
                    <li>Contact lists</li>
                    <li>Campaign data</li>
                    <li>Consent records</li>
                  </ul>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowExportDialog(false)}>Cancel</Button>
                  <Button onClick={exportData} data-testid="confirm-export-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Download Data
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete Account */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogTrigger asChild>
                <div className="p-4 border rounded-lg hover:bg-red-50 cursor-pointer transition border-red-200" data-testid="delete-data-card">
                  <Trash2 className="w-8 h-8 text-red-600 mb-3" />
                  <h3 className="font-medium text-red-900">Delete My Data</h3>
                  <p className="text-sm text-red-700 mt-1">Permanently delete all your data</p>
                </div>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-red-600">Delete All Data</DialogTitle>
                  <DialogDescription>
                    This action is irreversible. All your data will be permanently deleted.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <AlertTriangle className="w-6 h-6 text-red-600 mb-2" />
                    <p className="text-sm text-red-800">
                      <strong>Warning:</strong> This will delete your account, all call history, contacts, campaigns, and any other data associated with your account. This cannot be undone.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
                  <Button variant="destructive" onClick={requestDeletion} data-testid="confirm-delete-btn">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Everything
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Submit Request */}
            <Dialog>
              <DialogTrigger asChild>
                <div className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition" data-testid="submit-request-card">
                  <FileText className="w-8 h-8 text-emerald-600 mb-3" />
                  <h3 className="font-medium">Submit Request</h3>
                  <p className="text-sm text-slate-500 mt-1">Submit a data access or correction request</p>
                </div>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Submit Data Request</DialogTitle>
                  <DialogDescription>
                    Submit a formal data subject access request (DSAR)
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Request Type</Label>
                    <Select value={dsarType} onValueChange={setDsarType}>
                      <SelectTrigger data-testid="dsar-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="access">Access my data</SelectItem>
                        <SelectItem value="rectification">Correct my data</SelectItem>
                        <SelectItem value="restriction">Restrict processing</SelectItem>
                        <SelectItem value="objection">Object to processing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Details (optional)</Label>
                    <Textarea
                      placeholder="Provide any additional details about your request..."
                      value={dsarDetails}
                      onChange={(e) => setDsarDetails(e.target.value)}
                      data-testid="dsar-details-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={submitDsar} data-testid="submit-dsar-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Submit Request
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Consent History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Consent History
          </CardTitle>
          <CardDescription>Record of your privacy preference changes</CardDescription>
        </CardHeader>
        <CardContent>
          {consentHistory.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No consent changes recorded yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {consentHistory.slice(0, 10).map((record) => (
                <div key={record.id} className="flex items-center justify-between p-3 border rounded-lg text-sm" data-testid={`consent-history-${record.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${record.granted ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <div>
                      <span className="font-medium capitalize">{record.consent_type.replace(/_/g, " ")}</span>
                      <span className="text-slate-500 ml-2">{record.granted ? "enabled" : "disabled"}</span>
                    </div>
                  </div>
                  <span className="text-slate-400">{new Date(record.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Regional Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Your Privacy Rights by Region
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="font-medium text-red-900">Canada (PIPEDA)</p>
              <p className="text-red-700 mt-1">Right to access, correct, and withdraw consent</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="font-medium text-blue-900">USA (CCPA/CPRA)</p>
              <p className="text-blue-700 mt-1">Right to know, delete, opt-out of sale</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg">
              <p className="font-medium text-indigo-900">Europe (GDPR)</p>
              <p className="text-indigo-700 mt-1">Right to erasure, portability, restriction</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg">
              <p className="font-medium text-emerald-900">UAE (PDPL)</p>
              <p className="text-emerald-700 mt-1">Right to access, rectify, erase personal data</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
